import { ExportOutlined } from "@ant-design/icons"
import { type TableColumnsType, Tag, Typography } from "antd"
import React from "react"
import { Link } from "react-router-dom"

import { RecordRef } from "../../../../../components/RecordRef.tsx"
import { empty } from "../../../../../constants/placeholders"
import { RECORD_ID_LABEL } from "../../../../../constants/record-ref.ts"
import { RoutesPaths } from "../../../../../constants/routes.ts"
import {
  LEAD_STATUSES,
  LEAD_STATUSES_MAP,
  leadStatusColorMap,
  type LeadStatusesValues
} from "../../../../Lead/constants/lead-statuses.ts"
import { LeadFollowUpCell } from "../../../components/LeadFollowUpCell.tsx"
import { shortRelativeTime } from "../../../helpers/lead-table-format.ts"
import { type LeadListItem } from "../../../types/types.ts"
import { getEntityName } from "../../../../../utils/user/get-entity-name"
import { getLeadsColumnActions } from "./column-actions"

const StatusTag = ({ stage }: { stage?: number | null }) => {
  const status = LEAD_STATUSES_MAP[stage as LeadStatusesValues]
  if (!status) return empty

  return (
    <Tag style={leadStatusColorMap[stage as LeadStatusesValues]}>
      {status.label}
    </Tag>
  )
}

const { Text, Paragraph } = Typography

interface GetColumnsArg {
  deleteAllowed?: boolean
  editAllowed?: boolean
  onSelectRow: (record: LeadListItem, action: string, payload?: unknown) => void
}

export const getColumns = ({
  onSelectRow,
  deleteAllowed,
  editAllowed
}: GetColumnsArg): TableColumnsType<LeadListItem> => [
  {
    title: RECORD_ID_LABEL,
    dataIndex: "uid",
    key: "uid",
    width: 140,
    render: (uid: string | null) => <RecordRef uid={uid} compact short />
  },
  {
    // Mirrors the active Leads list's Customer column so an archived lead
    // still names who it belongs to. `temporary_customer` covers a lead
    // created before its customer record was linked. Same `GET /leads/`
    // endpoint as the active list, so the same `customer__first_name`
    // ordering works.
    title: "Customer",
    key: "customer",
    dataIndex: "customer__first_name",
    sorter: true,
    width: 200,
    render: (_: unknown, record) => {
      const entity = record?.customer || record?.temporary_customer
      if (!entity) return empty

      const name = getEntityName(entity)
      return <Text ellipsis={{ tooltip: name }}>{name}</Text>
    }
  },
  {
    title: "Lead Name",
    dataIndex: "lead_name",
    key: "lead_name",
    sorter: true,
    width: 220,
    render: (value: string, lead) => (
      <Link to={RoutesPaths.lead.view(lead?.id)}>
        <Text className={"text-inherit"} ellipsis={{ tooltip: value }}>
          {value}
        </Text>
      </Link>
    )
  },
  {
    // Necessary now that tabs are gone — status used to be implied by which
    // tab was open. Deliberately `current_status`, not the pre-existing
    // `status` field, which resolves to a linked Task's own workflow status
    // rather than the lead's pipeline stage.
    title: "Current Status",
    dataIndex: "current_status",
    key: "current_status",
    width: 150,
    render: (stage: number | null) => <StatusTag stage={stage} />
  },
  {
    // Same underlying field as before, renamed to match the redesign.
    title: "Last Status",
    dataIndex: "previous_stage",
    key: "previous_stage",
    width: 150,
    render: (stage: number | null) => <StatusTag stage={stage} />
  },
  {
    title: "Inactive Reason",
    dataIndex: "inactive_reason",
    key: "inactive_reason",
    width: 220,
    render: (reason: string | null, record) => {
      const value = reason || record?.lost_reason

      if (!value) return empty

      return (
        <Paragraph type={"secondary"} ellipsis={{ rows: 2, tooltip: value }}>
          {value}
        </Paragraph>
      )
    }
  },
  {
    title: "Inactive Since",
    dataIndex: "inactive_since",
    key: "inactive_since",
    sorter: true,
    width: 150,
    render: (since: string | null, record) => {
      const value = since || record?.archived_at || record?.lost_since

      if (!value) return empty

      return (
        <Text ellipsis={{ tooltip: value }}>{shortRelativeTime(value)}</Text>
      )
    }
  },
  {
    title: "Moved to Inactive By",
    dataIndex: "moved_to_inactive_by",
    key: "moved_to_inactive_by",
    width: 180,
    // Blank is expected, not a gap: a row archived outside the endpoints
    // leaves no audit trail, and Won is a conversion rather than an archive.
    render: (name: string | null) =>
      name ? <Text ellipsis={{ tooltip: name }}>{name}</Text> : empty
  },
  {
    title: "Next Follow Up",
    dataIndex: "follow_up_date",
    key: "follow_up_date",
    sorter: true,
    width: 180,
    render: (_, record) => (
      <LeadFollowUpCell
        date={record?.follow_up_date}
        time={record?.follow_up_time}
        nurture={record?.current_status === LEAD_STATUSES.NURTURING}
      />
    )
  },
  {
    title: "Notes",
    dataIndex: "comments",
    key: "comments",
    width: 260,
    render: (comments: string | null, record) => {
      // Won/Converted has no notes of its own — the cell points at the
      // project it became instead, since there is nothing to reverse here.
      if (record?.is_project) {
        return (
          <Link
            to={RoutesPaths.project.view(record.id).overview}
            className={"inline-flex items-center gap-1"}
          >
            View Project <ExportOutlined className={"text-xs"} />
          </Link>
        )
      }

      return comments ? (
        <Paragraph type={"secondary"} ellipsis={{ rows: 2, tooltip: comments }}>
          {comments}
        </Paragraph>
      ) : (
        empty
      )
    }
  },

  getLeadsColumnActions({ onSelectRow, deleteAllowed, editAllowed })
]
